import { useNavigate } from "react-router";

import { useLogout } from "@/lib/api";
import { clearDemoPersona } from "@/lib/demo-persona-store";
import { clearKioskPerson } from "@/lib/kiosk-person";
import { logger } from "@/lib/logger";

/**
 * The one sign-out action: end the session, then go to the login page (which
 * waits for a click, rather than signing them straight back in).
 *
 * Shared by the header's user menu and the "Filing as … Not you? Sign out"
 * line on the filing forms, so both leave the same way. On a shared device it
 * also forgets who was at it, so the next sign-in starts at "who are you?"
 * (1 Oct).
 */
export function useSignOut(): { signOut: () => Promise<void>; isPending: boolean } {
  const navigate = useNavigate();
  const logoutMutation = useLogout();

  const signOut = async () => {
    logger.debug("[auth] signing out");
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      // The cookie may already be gone; either way the user asked to leave, so
      // send them to the login page rather than trapping them in the shell.
      logger.warn("[auth] sign-out request failed", error);
    }
    clearKioskPerson();
    clearDemoPersona();
    // `signed_out`: the login page waits for a click instead of signing
    // straight back in (28 Oct).
    void navigate("/login?signed_out=1", { replace: true });
  };

  return { signOut, isPending: logoutMutation.isPending };
}
